import { GetServerSidePropsContext } from 'next'

import { isCurrentStage } from '@/core/utils/environment'
import { setCookie } from '@/core/utils/serverSide'

import {
  DEVTOOLS_QUERY_PARAM,
  DEVTOOLS_TOKEN_COOKIE_NAME,
  DEVTOOLS_TOKEN_MS,
} from './constants'
import { checkTokenInKeystone } from './keystoneTokenCheck'
import {
  isDevToolsQueryValueEnabled,
  isDevToolsQueryValueExplicitlyDisabled,
} from './queryParamHandler'
import { getTokenExpirationTime, isValidTokenFormat } from './tokenValidation'

/* eslint-disable security/detect-object-injection -- parses Cookie header and reads fixed DevTools cookie / query keys */

export interface DevToolsAuthResult {
  isEnabled: boolean
}

/**
 * Parses cookie header string into key-value object
 */
export const parseCookies = (
  cookieHeader: string | undefined
): Record<string, string> => {
  if (!cookieHeader) return {}

  return cookieHeader.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=')
    if (key && value) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, string>)
}

/**
 * Handles DevTools authentication using token-based flow
 * Validates token from devtools_token cookie against Keystone
 *
 * @param context - Next.js server-side props context
 * @returns DevToolsAuthResult with isEnabled flag
 */
export const handleDevToolsAuth = async (
  context: GetServerSidePropsContext
): Promise<DevToolsAuthResult> => {
  const { req, res } = context
  const queryVal = context.query[DEVTOOLS_QUERY_PARAM]

  if (isDevToolsQueryValueExplicitlyDisabled(queryVal)) {
    return { isEnabled: false }
  }

  if (!isCurrentStage('prod')) {
    return { isEnabled: isDevToolsQueryValueEnabled(queryVal) }
  }

  const cookies = parseCookies(req.headers.cookie)
  const token = cookies[DEVTOOLS_TOKEN_COOKIE_NAME]

  if (!token) {
    return { isEnabled: false }
  }

  try {
    // Validate token format
    if (!isValidTokenFormat(token)) {
      setCookie(res, DEVTOOLS_TOKEN_COOKIE_NAME, '', {
        maxAge: 0,
        strict: true,
      })
      return { isEnabled: false }
    }

    // Check token in Keystone (handles expiration validation and caching)
    const keystoneCheck = await checkTokenInKeystone(token, context)

    if (
      keystoneCheck.isExpired ||
      !keystoneCheck.isValid ||
      !keystoneCheck.tokenFound
    ) {
      setCookie(res, DEVTOOLS_TOKEN_COOKIE_NAME, '', {
        maxAge: 0,
        strict: true,
      })
      return { isEnabled: false }
    }

    // Token is valid - update cookie expiration to match token expiration
    const tokenExpirationTime = getTokenExpirationTime(token)
    const now = Date.now()

    if (tokenExpirationTime && tokenExpirationTime > now) {
      const maxAgeSeconds = Math.floor((tokenExpirationTime - now) / DEVTOOLS_TOKEN_MS)
      setCookie(res, DEVTOOLS_TOKEN_COOKIE_NAME, token, {
        maxAge: maxAgeSeconds,
        strict: true,
        httpOnly: false,
      })
    }

    return { isEnabled: true }
  } catch (error) {
    console.error('prod', 'Error during DevTools token validation', {
      error: error instanceof Error ? error.message : 'Unknown error',
    })
    return { isEnabled: false }
  }
}
